/**
 * useAppLock — bloqueio biométrico opcional do app.
 *
 * Regras:
 * - Preferência guardada localmente (por dispositivo), nunca no servidor:
 *   é uma trava do aparelho, não da conta.
 * - O app trava no boot e sempre que volta do background após 60s.
 * - Web/PWA: sempre destravado (biometria indisponível) — nada quebra.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { getBiometricAvailability, verifyBiometric } from "@/lib/native/biometric";
import { isNative } from "@/lib/platform";

const STORAGE_KEY = "zappy_app_lock_enabled";
const BACKGROUND_RELOCK_MS = 60_000;

export function useAppLock() {
  const [enabled, setEnabled] = useState<boolean>(() => {
    try {
      return localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const [available, setAvailable] = useState(false);
  const [label, setLabel] = useState("Biometria");
  const [unavailableReason, setUnavailableReason] = useState<string | undefined>();
  const [locked, setLocked] = useState<boolean>(() => {
    try {
      return isNative() && localStorage.getItem(STORAGE_KEY) === "1";
    } catch {
      return false;
    }
  });
  const hiddenSince = useRef<number | null>(null);

  useEffect(() => {
    let alive = true;
    getBiometricAvailability().then((res) => {
      if (!alive) return;
      setAvailable(res.available);
      setLabel(res.label);
      setUnavailableReason(res.reason);
      if (!res.available) setLocked(false);
    });
    return () => {
      alive = false;
    };
  }, []);

  const unlock = useCallback(async () => {
    const ok = await verifyBiometric("Confirme que é você para abrir o Zappy.");
    if (ok) setLocked(false);
    return ok;
  }, []);

  const setLockEnabled = useCallback(
    async (next: boolean) => {
      if (next) {
        const ok = await verifyBiometric("Confirme sua biometria para ativar o bloqueio do Zappy.");
        if (!ok) return false;
      }
      setEnabled(next);
      try {
        localStorage.setItem(STORAGE_KEY, next ? "1" : "0");
      } catch {
        /* storage indisponível — mantém só em memória */
      }
      return true;
    },
    [],
  );

  // Re-trava ao voltar do background.
  useEffect(() => {
    if (!enabled || !available) return;
    const onVisibility = () => {
      if (document.visibilityState === "hidden") {
        hiddenSince.current = Date.now();
        return;
      }
      if (hiddenSince.current && Date.now() - hiddenSince.current >= BACKGROUND_RELOCK_MS) {
        setLocked(true);
      }
      hiddenSince.current = null;
    };
    document.addEventListener("visibilitychange", onVisibility);
    return () => document.removeEventListener("visibilitychange", onVisibility);
  }, [enabled, available]);

  return {
    enabled,
    available,
    label,
    unavailableReason,
    locked: locked && enabled && available,
    unlock,
    setLockEnabled,
  };
}
